import React, { useEffect, useState } from "react";
import { alpha, styled, useTheme } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import SvgIcon from "@mui/material/SvgIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { Link as RouterLink, useLocation } from "react-router";
import { useAuth } from "../Contexts/AuthContext";
import VersionDialog from "../Components/versionDialog/VersionDialog";

const drawerWidth = 240;

// Mixin for closed drawer width
const closedMixin = (theme: any) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden" as const,
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

// Mixin for open drawer width
const openedMixin = (theme: any) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
});

// Styled Drawer with enhanced styling
const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.surface?.card ?? theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.surface?.border ?? theme.palette.divider}`,
    boxShadow:
      theme.palette.mode === "light"
        ? "2px 0 8px rgba(0, 0, 0, 0.06)"
        : "2px 0 8px rgba(0, 0, 0, 0.2)",
  },
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      backgroundColor: theme.palette.surface?.card ?? theme.palette.background.paper,
      borderRight: `1px solid ${theme.palette.surface?.border ?? theme.palette.divider}`,
      boxShadow:
        theme.palette.mode === "light"
          ? "2px 0 8px rgba(0, 0, 0, 0.06)"
          : "2px 0 8px rgba(0, 0, 0, 0.2)",
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      backgroundColor: theme.palette.surface?.card ?? theme.palette.background.paper,
      borderRight: `1px solid ${theme.palette.surface?.border ?? theme.palette.divider}`,
      boxShadow:
        theme.palette.mode === "light"
          ? "2px 0 8px rgba(0, 0, 0, 0.06)"
          : "2px 0 8px rgba(0, 0, 0, 0.2)",
    },
  }),
}));

// Drawer header with accent border
const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 8px",
  backgroundColor: theme.palette.surface?.elevated ?? theme.palette.background.default,
  borderBottom: `2px solid ${theme.palette.accent?.main ?? "#F59E0B"}`,
  ...theme.mixins.toolbar,
}));

// Close button with hover styling
const CloseButton = styled(IconButton)(({ theme }) => ({
  transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: `${theme.palette.accent?.main ?? "#F59E0B"}20`,
    "& .MuiSvgIcon-root": {
      color: theme.palette.accent?.main ?? "#F59E0B",
    },
  },
}));

// Styled navigation item with active state
const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "nested",
})<{ active?: boolean; nested?: boolean; component?: React.ElementType }>(({ theme, active, nested }) => ({
  marginLeft: 8,
  marginRight: 8,
  marginTop: 2,
  marginBottom: 2,
  borderRadius: 8,
  minHeight: nested ? 42 : 48,
  paddingLeft: nested ? theme.spacing(4) : theme.spacing(2),
  position: "relative",
  transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  backgroundColor: active
    ? `${theme.palette.accent?.main ?? "#F59E0B"}15`
    : "transparent",
  "&::before": active
    ? {
        content: '""',
        position: "absolute",
        left: 0,
        top: "50%",
        transform: "translateY(-50%)",
        width: 4,
        height: "60%",
        borderRadius: "0 4px 4px 0",
        backgroundColor: theme.palette.accent?.main ?? "#F59E0B",
      }
    : {},
  "&:hover": {
    backgroundColor: active
      ? `${theme.palette.accent?.main ?? "#F59E0B"}20`
      : theme.palette.action.hover,
  },
  "& .MuiListItemIcon-root": {
    transition: "transform 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  },
  "&:hover .MuiListItemIcon-root": {
    transform: "scale(1.1)",
  },
}));

// Styled navigation icon
const StyledListItemIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "nested",
})<{ active?: boolean; nested?: boolean }>(({ theme, active, nested }) => ({
  minWidth: 40,
  "& .MuiSvgIcon-root, & .MuiIcon-root": {
    fontSize: nested ? 18 : 22,
    color: active
      ? theme.palette.accent?.main ?? "#F59E0B"
      : theme.palette.text.secondary,
    transition: "color 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  },
}));

// Group expand icon container
const ExpandIconContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded?: boolean }>(({ theme, expanded }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  height: 28,
  borderRadius: 4,
  backgroundColor: theme.palette.surface?.elevated ?? theme.palette.action.hover,
  transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
  "& .MuiSvgIcon-root": {
    fontSize: 18,
    transition: "transform 250ms cubic-bezier(0.4, 0, 0.2, 1)",
    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
  },
}));

// Navigation group divider
const NavGroupDivider = styled(Divider)(({ theme }) => ({
  marginLeft: 16,
  marginRight: 16,
  marginTop: 8,
  marginBottom: 8,
  opacity: 0.6,
}));

interface PrefetchConfig {
  componentByPath: Map<string, string>;
  onPrefetch: (componentPath: string) => Promise<void> | void;
}

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  prefetch?: PrefetchConfig;
  onNavigateStart?: (path: string, source?: string) => void;
}

function Sidebar({
  sidebarOpen,
  setSidebarOpen,
  prefetch,
  onNavigateStart,
}: SidebarProps) {
  const theme = useTheme();
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const [openVersion, setOpenVersion] = useState(false);
  const [collapsedMenu, setCollapsedMenu] = useState<{
    groupKey: string;
    anchorEl: HTMLElement;
  } | null>(null);
  const Auth: any = useAuth();

  const handleDrawerClose = () => {
    setSidebarOpen(false);
  };

  useEffect(() => {
    setCollapsedMenu(null);
  }, [location.pathname]);

  const handleGroupClick = (event: React.MouseEvent<HTMLElement>, groupName: string) => {
    if (!sidebarOpen) {
      setCollapsedMenu((current) =>
        current?.groupKey === groupName ? null : { groupKey: groupName, anchorEl: event.currentTarget }
      );
      return;
    }

    const index = openGroups.indexOf(groupName);
    if (index >= 0) {
      const newGroups = [...openGroups];
      newGroups.splice(index, 1);
      setOpenGroups(newGroups);
    } else {
      setOpenGroups([...openGroups, groupName]);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  return (
    <StyledDrawer variant="permanent" open={sidebarOpen}>
      <DrawerHeader>
        <Box
          component="img"
          src="/assets/logo.png"
          alt="pms-logo"
          sx={{ width: 85, height: "auto" }}
        />
        <CloseButton onClick={handleDrawerClose} size="large">
          {theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </CloseButton>
      </DrawerHeader>
      <List
        component="div"
        disablePadding
        sx={{ pt: 1, flex: 1, overflowY: "auto", overflowX: "hidden" }}
      >
        {Auth.AccessibleApps?.map((app: any, index: number) => {
          if (app.ShowInNavigationTree !== 1 && app.ShowInNavigationTree !== true) {
            return null;
          }

          const appPath = app.Path + (app.params ? "/" + app.params : "");
          const groupKey = "G" + app.Application;
          const isGroupActive = app.childApps?.some((child: any) =>
            isActive(child.Path + (child.params ? "/" + child.params : ""))
          );

          if (app.childApps) {
            // Group with children
            return (
              <React.Fragment key={groupKey}>
                {index > 0 && <NavGroupDivider />}
                <NavItemWrapper sidebarOpen={sidebarOpen} label={app.Application}>
                  <StyledListItemButton
                    onClick={(event) => handleGroupClick(event, groupKey)}
                    active={isGroupActive}
                  >
                    <StyledListItemIcon active={isGroupActive}>
                      <NavIcon icon={app.Icon} />
                    </StyledListItemIcon>
                    <ListItemText
                      primary={app.Application}
                      sx={{
                        opacity: sidebarOpen ? 1 : 0,
                        transition: "opacity 200ms",
                      }}
                    />
                    {sidebarOpen && (
                      <ExpandIconContainer expanded={openGroups.includes(groupKey)}>
                        <ExpandMoreIcon />
                      </ExpandIconContainer>
                    )}
                  </StyledListItemButton>
                </NavItemWrapper>
                <Collapse in={openGroups.includes(groupKey) && sidebarOpen} timeout="auto" unmountOnExit>
                  {app.childApps.map((childApp: any) => {
                    const childPath = childApp.Path + (childApp.params ? "/" + childApp.params : "");
                    const childActive = isActive(childPath);
                    return (
                      <ListItemLink
                        key={childPath}
                        to={childPath}
                        primary={childApp.Application}
                        icon={childApp.Icon}
                        nested
                        active={childActive}
                        sidebarOpen={sidebarOpen}
                        componentPath={prefetch?.componentByPath.get(childPath)}
                        onNavigateStart={onNavigateStart}
                        onPrefetch={prefetch?.onPrefetch}
                      />
                    );
                  })}
                </Collapse>
                {!sidebarOpen && (
                  <Menu
                    anchorEl={collapsedMenu?.groupKey === groupKey ? collapsedMenu.anchorEl : null}
                    open={collapsedMenu?.groupKey === groupKey}
                    onClose={() => setCollapsedMenu(null)}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    slotProps={{
                      paper: {
                        sx: {
                          minWidth: 220,
                        },
                      },
                    }}
                  >
                    {app.childApps.map((childApp: any) => {
                      const childPath = childApp.Path + (childApp.params ? "/" + childApp.params : "");
                      const childActive = isActive(childPath);
                      return (
                        <CollapsedMenuItemLink
                          key={childPath}
                          to={childPath}
                          primary={childApp.Application}
                          icon={childApp.Icon}
                          active={childActive}
                          componentPath={prefetch?.componentByPath.get(childPath)}
                          onNavigateStart={onNavigateStart}
                          onPrefetch={prefetch?.onPrefetch}
                          onClose={() => setCollapsedMenu(null)}
                        />
                      );
                    })}
                  </Menu>
                )}
              </React.Fragment>
            );
          }

          // Single item
          const itemActive = isActive(appPath);
          return (
            <React.Fragment key={appPath}>
              {index > 0 && <NavGroupDivider />}
              <ListItemLink
                to={appPath}
                primary={app.Application}
                icon={app.Icon}
                active={itemActive}
                sidebarOpen={sidebarOpen}
                componentPath={prefetch?.componentByPath.get(appPath)}
                onNavigateStart={onNavigateStart}
                onPrefetch={prefetch?.onPrefetch}
              />
            </React.Fragment>
          );
        })}
      </List>
      <Box
        component="footer"
        onClick={() => setOpenVersion(true)}
        sx={{
          borderTop: (currentTheme) => `1px solid ${currentTheme.palette.divider}`,
          background: (currentTheme) => alpha(currentTheme.palette.action.hover, 0.5),
          width: "100%",
          py: 0.5,
          textAlign: "center",
          opacity: sidebarOpen ? 1 : 0,
          transition: "all 0.3s linear",
          cursor: "pointer",
        }}
      >
        <Typography variant="caption" sx={{ textAlign: "center" }}>
          <Box component="span" sx={{ fontWeight: 700 }}>
            LogiPro PMS
          </Box>{" "}
          -{" "}
          <Box component="span" sx={{ fontSize: 10 }}>
            Version {import.meta.env.VITE_VERSION}
          </Box>
        </Typography>
      </Box>
      <VersionDialog open={openVersion} onClose={() => setOpenVersion(false)} />
    </StyledDrawer>
  );
}

export default Sidebar;

// Wrapper for tooltip on collapsed state
interface NavItemWrapperProps {
  children: React.ReactNode;
  sidebarOpen: boolean;
  label: string;
}

const NavItemWrapper: React.FC<NavItemWrapperProps> = ({ children, sidebarOpen, label }) => {
  if (sidebarOpen) {
    return <>{children}</>;
  }
  return (
    <Tooltip title={label} placement="right" arrow>
      <Box>{children}</Box>
    </Tooltip>
  );
};

interface ListItemLinkProps {
  to: string;
  icon: string;
  primary: string;
  nested?: boolean;
  active?: boolean;
  sidebarOpen?: boolean;
  componentPath?: string;
  onNavigateStart?: (path: string, source?: string) => void;
  onPrefetch?: (componentPath: string) => Promise<void> | void;
}

const ListItemLink: React.FC<ListItemLinkProps> = ({
  to,
  icon,
  primary,
  nested,
  active,
  sidebarOpen = true,
  componentPath,
  onNavigateStart,
  onPrefetch,
}) => {
  const prefetch = React.useCallback(() => {
    if (!componentPath || !onPrefetch) {
      return;
    }
    void onPrefetch(componentPath);
  }, [componentPath, onPrefetch]);

  const handleClick = React.useCallback(() => {
    // Skip navigation progress if already on this route
    if (window.location.pathname === to) return;
    onNavigateStart?.(to, "sidebar");
  }, [onNavigateStart, to]);

  const renderLink = React.useMemo(() => {
    const LinkComponent = React.forwardRef<
      HTMLAnchorElement,
      Omit<React.ComponentPropsWithoutRef<typeof RouterLink>, "to">
    >((itemProps, ref) => <RouterLink to={to} {...itemProps} ref={ref} />);
    LinkComponent.displayName = "ListItemLinkRouter";
    return LinkComponent;
  }, [to]);

  const content = (
    <li>
      <StyledListItemButton
        component={renderLink}
        active={active}
        nested={nested}
        sx={{ color: "inherit", textDecoration: "none" }}
        onClick={handleClick}
        onMouseEnter={prefetch}
        onFocus={prefetch}
        onTouchStart={prefetch}
      >
        <StyledListItemIcon active={active} nested={nested}>
          <NavIcon icon={icon} />
        </StyledListItemIcon>
        <ListItemText
          primary={primary}
          sx={{
            opacity: sidebarOpen ? 1 : 0,
            transition: "opacity 200ms",
          }}
        />
      </StyledListItemButton>
    </li>
  );

  if (!sidebarOpen) {
    return (
      <Tooltip title={primary} placement="right" arrow>
        {content}
      </Tooltip>
    );
  }

  return content;
};

function NavIcon({ icon }: { icon: string }) {
  if (icon.startsWith("SVG")) {
    return (
      <SvgIcon>
        <path d={icon.substring(3)} />
      </SvgIcon>
    );
  }
  return <Icon>{icon}</Icon>;
}

interface CollapsedMenuItemLinkProps {
  to: string;
  icon: string;
  primary: string;
  active?: boolean;
  componentPath?: string;
  onNavigateStart?: (path: string, source?: string) => void;
  onPrefetch?: (componentPath: string) => Promise<void> | void;
  onClose: () => void;
}

const CollapsedMenuItemLink: React.FC<CollapsedMenuItemLinkProps> = ({
  to,
  icon,
  primary,
  active,
  componentPath,
  onNavigateStart,
  onPrefetch,
  onClose,
}) => {
  const prefetch = React.useCallback(() => {
    if (!componentPath || !onPrefetch) {
      return;
    }
    void onPrefetch(componentPath);
  }, [componentPath, onPrefetch]);

  const handleClick = React.useCallback(() => {
    if (window.location.pathname !== to) {
      onNavigateStart?.(to, "sidebar");
    }
    onClose();
  }, [onClose, onNavigateStart, to]);

  return (
    <MenuItem
      component={RouterLink}
      to={to}
      selected={active}
      onClick={handleClick}
      onMouseEnter={prefetch}
      onFocus={prefetch}
      onTouchStart={prefetch}
    >
      <ListItemIcon>
        <NavIcon icon={icon} />
      </ListItemIcon>
      <ListItemText>{primary}</ListItemText>
    </MenuItem>
  );
};
